import { EventEmitter } from '../event-emitter'
import type { ICommand } from './types'

export class UndoManager {
  #commandsLimit = 100
  #eventEmmitter = new EventEmitter()

  #activeCommandIndex = -1
  #commands: ICommand[] = []

  emitChange() {
    this.#eventEmmitter.emit('change')
  }

  add(command: ICommand) {
    if (this.#activeCommandIndex < this.#commands.length - 1) {
      this.#commands = this.#commands.slice(
        0,
        Math.max(this.#activeCommandIndex + 1, 0)
      )
    }

    this.#commands.push(command)

    if (this.#commands.length > this.#commandsLimit) {
      this.#commands.shift()
    }

    this.#activeCommandIndex = this.#commands.length - 1

    this.emitChange()
  }

  undo() {
    if (!this.undoAvailable()) {
      return
    }

    const activeCommand = this.#commands[this.#activeCommandIndex]

    activeCommand.undo()

    this.#activeCommandIndex = Math.max(this.#activeCommandIndex - 1, -1)
    this.#eventEmmitter.emit('change')
  }

  redo() {
    if (!this.redoAvailable()) {
      return
    }

    const activeCommand = this.#commands[this.#activeCommandIndex + 1]

    activeCommand.redo()

    this.#activeCommandIndex = Math.min(
      this.#activeCommandIndex + 1,
      this.#commands.length - 1
    )

    this.#eventEmmitter.emit('change')
  }

  clear() {
    this.#activeCommandIndex = -1
    this.#commands = []
  }

  undoAvailable() {
    return this.#activeCommandIndex > -1
  }

  redoAvailable() {
    return this.#activeCommandIndex + 1 < this.#commands.length
  }

  terminate() {
    this.clear()
    this.#eventEmmitter.clear()
  }

  onChange(callback: () => void) {
    this.#eventEmmitter.on('change', callback)
  }

  offChange(callback: () => void) {
    this.#eventEmmitter.off('change', callback)
  }
}
